import { Stack } from '@mantine/core';
import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnRenderProps } from 'react-table';

import type { IColumn, TTheme } from '@feathr/components';
import { ContextMenu, ProgressBar, TableColumnHeader, Text } from '@feathr/components';
import { getIconForAction } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISampleInvitesCampaign } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';
import buildPillColumn from './buildPillColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const STATUS_THEME: Record<ISampleInvitesCampaign['status'], TTheme> = {
  draft: 'neutral',
  live: 'green',
  completed: 'violet',
};

const STATUS_LABEL: Record<ISampleInvitesCampaign['status'], string> = {
  draft: 'Draft',
  live: 'Live',
  completed: 'Completed',
};

interface IInvitesCampaignColumnsArgs {
  base: string;
  t: TFunction;
}

function invitesCampaignColumns({
  base,
  t,
}: IInvitesCampaignColumnsArgs): Array<IColumn<ISampleInvitesCampaign>> {
  return [
    {
      accessor: 'name',
      id: 'name',
      Header: TableColumnHeader({ title: t('Name'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 280,
      Cell({ original }): JSX.Element {
        return (
          <>
            <Link to={base}>{original.name}</Link>
            <Text variant={'smallRegular'}>
              {t('in {{project}}', { project: original.project })}
            </Text>
          </>
        );
      },
      Footer(): JSX.Element {
        return <strong>{t('Page totals')}</strong>;
      },
    },
    {
      accessor: (row): number =>
        row.totalPartners > 0 ? row.acceptedPartners / row.totalPartners : 0,
      id: 'partners',
      Header: TableColumnHeader({ title: t('Partners accepted'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      width: 220,
      Cell({ original }): JSX.Element {
        const ratio =
          original.totalPartners > 0 ? original.acceptedPartners / original.totalPartners : 0;
        const percent = Math.round(ratio * 100);
        return (
          <Stack gap={'xs'}>
            <Text variant={'defaultMedium'}>
              {t('{{accepted}} / {{total}}', {
                accepted: original.acceptedPartners,
                total: original.totalPartners,
              })}
            </Text>
            <ProgressBar
              aria-label={t('{{percent}}% accepted', { percent })}
              theme={'green'}
              value={percent}
              width={'100%'}
            />
          </Stack>
        );
      },
    },
    {
      accessor: 'reach',
      id: 'reach',
      Header: TableColumnHeader({ title: t('Reach'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.reach).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleInvitesCampaign }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.reach, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    buildPillColumn<ISampleInvitesCampaign, ISampleInvitesCampaign['status']>({
      accessor: 'status',
      labelMap: STATUS_LABEL,
      t,
      themeMap: STATUS_THEME,
      title: t('Status'),
      width: 120,
    }),
    buildOptionsColumn<ISampleInvitesCampaign>({
      t,
      renderItems: (): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('edit')}>
            {t('Edit')}
          </ContextMenu.Item>
          <DemoMenuItem action={'duplicate'} label={t('Duplicate')} />
          <DemoMenuItem action={'stop'} isDestructive={true} label={t('Stop')} />
        </>
      ),
    }),
  ];
}

export default invitesCampaignColumns;
